/**
 * 文档列表页面
 * 展示用户的所有文档，支持搜索、分页、新建等功能
 */

import React, { useState, useEffect } from 'react';
import { Card, 
  Button, 
  Input, 
  Pagination, 
  Space,  
  Spin,
  Empty,
  Select,
  Tag,
  Row,
  Col,
  Statistic,
  Table,
  Tooltip,
  Popconfirm,
  Modal,
  Image
 } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import { 
  PlusOutlined, 
  SearchOutlined, 
  FileTextOutlined,
  LoadingOutlined,
  FileAddOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useDocumentListStyles } from '../styles/components/markdown/DocumentList';
import attachmentService from '../services/attachmentService';
import { markdownToFile, generateDocumentFilename, blobToMarkdown } from '../utils/markdownUtils';

import TemplateSelector from '../components/markdown/TemplateSelector';
import DocumentModal from '../components/markdown/DocumentModal';
import MarkdownViewer from '../components/markdown/MarkdownViewer';
import PDFViewer from '../components/common/PDFViewer';
import FilePreviewModal from '../components/common/FilePreviewModal';

const { Search } = Input;
const { Option } = Select;

const DocumentListPage = () => {
  const { styles } = useDocumentListStyles();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateSelectorVisible, setTemplateSelectorVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    archived: 0
  });
  
  // 文件预览相关状态
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Markdown查看器相关状态
  const [markdownViewerVisible, setMarkdownViewerVisible] = useState(false);
  const [markdownViewerDocument, setMarkdownViewerDocument] = useState(null);

  // 获取文档列表
  const fetchDocuments = async (page = 1, search = '', status = 'all', pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      // 使用搜索附件API获取文件列表
      const searchQuery = search || 'markdown'; // 默认搜索markdown文件
      const response = await attachmentService.searchAttachments(searchQuery, {
        page,
        per_page: pageSize,
        status: status === 'all' ? 'permanent' : status,
        content_type: 'text/markdown' // 只搜索markdown文件
      });
      
      // 直接使用后端返回的数据
      const attachments = response.attachments || [];
      
      setDocuments(attachments);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: pageSize,
        total: response.total || 0,
      }));

      // 更新统计数据
      updateStats(attachments);
    } catch (error) {
      window.message?.error('获取文档列表失败');
      console.error('获取文档列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 更新统计数据
  const updateStats = (attachments) => {
    const stats = {
      total: attachments.length,
      published: attachments.filter(att => att.status === 'permanent').length,
      draft: attachments.filter(att => att.status === 'temporary').length,
      archived: attachments.filter(att => att.status === 'archived').length
    };
    setStats(stats);
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  // 处理搜索
  const handleSearch = (value) => {
    setSearchText(value);
    // 如果搜索词为空，使用默认的markdown搜索
    const searchQuery = value.trim() || 'markdown';
    fetchDocuments(1, searchQuery, statusFilter, pagination.pageSize);
  };

  // 处理状态筛选
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    // 保持当前搜索词，如果为空则使用默认的markdown搜索
    const searchQuery = searchText.trim() || 'markdown';
    fetchDocuments(1, searchQuery, value, pagination.pageSize);
  };



  // 处理新建文档
  const handleNewDocument = () => {
    setTemplateSelectorVisible(true);
  };

  // 处理模板选择
  const handleTemplateSelect = (template) => {
    setTemplateSelectorVisible(false);
    setEditingDocument({
      title: template.name,
      content: template.content,
      template_id: template.id,
    });
    setDocumentModalVisible(true);
  };



  // 处理文档保存
  const handleDocumentSave = async (content, title, isMarkdownEditor = true, savedDocument = null) => {
    try {
      if (savedDocument) {
        // 如果有接口返回的数据，直接使用（这种情况通常来自DocumentModal）
      } else {
        // 原有的保存逻辑（兼容旧逻辑）
        if (editingDocument?.id) {
          // 更新现有文档
          const filename = generateDocumentFilename(title);
          const markdownFile = markdownToFile(content, filename);
          
          // 将文件内容转换为base64
          const fileContent = await blobToMarkdown(markdownFile);
          const base64Content = btoa(unescape(encodeURIComponent(fileContent)));
          
          // 使用附件更新API
          await attachmentService.updateAttachment(editingDocument.id, {
            file_content: base64Content,
            file_name: filename,
            description: `Markdown文档: ${title}`,
            content_type: 'markdown',
          });
          
          window.message?.success('文档更新成功');
        } else {
          // 创建新文档
          const filename = generateDocumentFilename(title);
          const markdownFile = markdownToFile(content, filename);
          
          // 使用附件上传API，根据是否使用Markdown编辑器设置editable字段
          await attachmentService.uploadAttachment(markdownFile, {
            description: `Markdown文档: ${title}`,
            editable: isMarkdownEditor, // 根据是否使用Markdown编辑器设置
            metadata: {
              source: isMarkdownEditor ? 'markdown_editor' : 'other_upload',
              category: 'document',
              title: title,
              template_id: editingDocument?.template_id,
              content_length: content.length,
              inserted_at: new Date().toISOString()
            }
          });
          
          window.message?.success('文档创建成功');
        }
      }
      
      // 关闭Modal并刷新列表
      setDocumentModalVisible(false);
      setEditingDocument(null);
      const searchQuery = searchText.trim() || 'markdown';
      fetchDocuments(pagination.current, searchQuery, statusFilter, pagination.pageSize);
    } catch (error) {
      console.error('保存文档失败:', error);
      throw error; // 让DocumentModal处理错误显示
    }
  };

  // 处理附件删除
  const handleAttachmentDelete = async (attachmentId) => {
    try {
      await attachmentService.deleteAttachment(attachmentId);
      window.message?.success('删除成功');
      const searchQuery = searchText.trim() || 'markdown';
      fetchDocuments(pagination.current, searchQuery, statusFilter, pagination.pageSize);
    } catch (error) {
      window.message?.error('删除失败');
      console.error('删除失败:', error);
    }
  };



  // 处理附件编辑
  const handleAttachmentEdit = async (attachment) => {
    try {
      
      // 优先使用fields.content，如果不存在则通过API获取
      let content = '';
      if (attachment.fields?.content) {
        content = attachment.fields.content;
      } else {
        // 使用attachmentService获取内容，避免CORS问题
        const response = await getAttachmentContent(attachment.id, attachment);
        
        // 将Blob转换为文本内容
        if (response instanceof Blob) {
          content = await response.text();
        } else {
          content = response;
        }
      }
      
      // 解析metadata
      let metadata = {};
      try {
        metadata = JSON.parse(attachment.metadata || '{}');
      } catch (e) {
        console.warn('解析metadata失败:', e);
      }

      setEditingDocument({
        id: attachment.id,
        title: metadata.title || attachment.file_name.replace(/\.[^/.]+$/, ''),
        content: content,
        // 传递完整的fields结构，确保DocumentModal能正确获取内容
        fields: attachment.fields,
        readOnly: false
      });
      setDocumentModalVisible(true);
    } catch (error) {
      window.message?.error('获取文件内容失败');
      console.error('获取文件内容失败:', error);
    }
  };

  // 获取附件内容，支持URL参数
  const getAttachmentContent = async (attachmentId, attachment = null) => {
    try {
      let token = null;
      
      // 如果提供了attachment对象，尝试从URL中提取token参数
      if (attachment && attachment.url) {
        try {
          const url = new URL(attachment.url);
          token = url.searchParams.get('img_token');
        } catch (e) {
          console.warn('解析URL参数失败:', e);
        }
      }
      
      // 使用attachmentService获取内容，保留URL参数
      const response = await attachmentService.downloadAttachment(attachmentId, token);
      return response;
    } catch (error) {
      console.error('获取附件内容失败:', error);
      throw error;
    }
  };

  // 处理附件查看详情
  const handleAttachmentView = async (attachment) => {
    try {
      const contentType = attachment.content_type;
      
      // 根据文件类型处理不同的预览方式
      if (contentType === 'text/markdown' || contentType === 'application/octet-stream') {
        // Markdown类型：显示Markdown编辑器
        handleMarkdownPreview(attachment);
      } else {
        // 其他类型：使用通用预览组件
        setPreviewFile(attachment);
        setPreviewVisible(true);
      }
    } catch (error) {
      window.message?.error('获取文件内容失败');
      console.error('获取文件内容失败:', error);
    }
  };

  // 处理Markdown预览
  const handleMarkdownPreview = async (attachment) => {
    try {
      // 获取Markdown内容
      const response = await getAttachmentContent(attachment.id, attachment);
      
      // 将Blob转换为文本内容
      let content = '';
      if (response instanceof Blob) {
        content = await response.text();
      } else {
        content = response;
      }
      
      // 解析metadata
      let metadata = {};
      try {
        metadata = JSON.parse(attachment.metadata || '{}');
      } catch (e) {
        console.warn('解析metadata失败:', e);
      }

      // 设置Markdown查看器文档
      const markdownDocument = {
        ...attachment,
        title: metadata.title || attachment.file_name.replace(/\.[^/.]+$/, ''),
        content: content,
        readOnly: true
      };

      setMarkdownViewerDocument(markdownDocument);
      setMarkdownViewerVisible(true);
    } catch (error) {
      window.message?.error('获取Markdown内容失败');
      console.error('获取Markdown内容失败:', error);
    }
  };

  // 处理Markdown查看器的编辑功能
  const handleMarkdownViewerEdit = () => {
    if (markdownViewerDocument) {
      setEditingDocument({
        id: markdownViewerDocument.id,
        title: markdownViewerDocument.title,
        content: markdownViewerDocument.content,
        readOnly: false
      });
      setMarkdownViewerVisible(false);
      setDocumentModalVisible(true);
    }
  };

  // 处理Markdown查看器的下载功能
  const handleMarkdownViewerDownload = () => {
    if (markdownViewerDocument) {
      try {
        const blob = new Blob([markdownViewerDocument.content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = markdownViewerDocument.file_name || `${markdownViewerDocument.title}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        window.message?.success('下载成功');
      } catch (error) {
        window.message?.error('下载失败');
        console.error('下载失败:', error);
      }
    }
  };

  // 获取文件类型图标
  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) {
      return <FileTextOutlined style={{ color: '#52c41a' }} />; // 绿色图标表示图片
    } else if (contentType === 'text/markdown') {
      return <FileTextOutlined style={{ color: '#1890ff' }} />; // 蓝色图标表示文档
    } else if (contentType.startsWith('video/')) {
      return <FileTextOutlined style={{ color: '#722ed1' }} />; // 紫色图标表示视频
    } else if (contentType === 'application/pdf') {
      return <FileTextOutlined style={{ color: '#ff4d4f' }} />; // 红色图标表示PDF
    } else if (contentType.startsWith('audio/')) {
      return <FileTextOutlined style={{ color: '#fa8c16' }} />; // 橙色图标表示音频
    } else {
      return <FileTextOutlined style={{ color: '#8c8c8c' }} />; // 灰色图标表示其他
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '文件名',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {getFileIcon(record.content_type)}
          <div style={{ marginLeft: 8 }}>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {record.content_type}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      render: (size) => {
        const units = ['B', 'KB', 'MB', 'GB'];
        let unitIndex = 0;
        let fileSize = size;
        
        while (fileSize >= 1024 && unitIndex < units.length - 1) {
          fileSize /= 1024;
          unitIndex++;
        }
        
        return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          permanent: { text: '永久', color: 'success' },
          temporary: { text: '临时', color: 'warning' },
          archived: { text: '已归档', color: 'default' }
        };
        const statusInfo = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (description) => description || '-',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => {
        const contentType = record.content_type;
        const isImage = contentType.startsWith('image/');
        const isVideo = contentType.startsWith('video/');
        const isMarkdown = contentType === 'text/markdown' || contentType === 'application/octet-stream';
        const isPDF = contentType === 'application/pdf';
        
        // 根据文件类型确定查看按钮的提示文本
        let viewTooltip = "查看详情";
        if (isImage) {
          viewTooltip = "预览图片";
        } else if (isVideo) {
          viewTooltip = "预览视频";
        } else if (isMarkdown) {
          viewTooltip = "查看文档";
        } else if (isPDF) {
          viewTooltip = "预览PDF";
        }
        
        return (
          <Space>
            <Tooltip title={viewTooltip}>
              <Button
                type="text"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleAttachmentView(record)}
              />
            </Tooltip>
            {isMarkdown && (
              <Tooltip title="编辑">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => handleAttachmentEdit(record)}
                />
              </Tooltip>
            )}
            <Popconfirm
              title="确定要删除这个文件吗？"
              onConfirm={() => handleAttachmentDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Tooltip title="删除">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // 获取状态筛选选项
  const getStatusFilterOptions = () => [
    { value: 'all', label: '全部', color: 'default' },
    { value: 'permanent', label: '永久', color: 'success' },
    { value: 'temporary', label: '临时', color: 'warning' },
    { value: 'archived', label: '已归档', color: 'default' },
  ];

  return (
    <div className={styles.container}>
      {/* 页面标题和操作栏 */}
      <div className={styles.header}>
        <h1 className={styles.title}>
        </h1>
        <Space>
          <Search
            placeholder="搜索文档..."
            className={styles.searchBar}
            onSearch={handleSearch}
            allowClear
            prefix={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNewDocument}
          >
            新建文档
          </Button>
        </Space>
      </div>

      {/* 筛选栏 */}
      <div className={styles.filterBar}>
        <Space>
          <span>状态筛选:</span>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            style={{ width: 120 }}
          >
            {getStatusFilterOptions().map(option => (
              <Option key={option.value} value={option.value}>
                <Tag color={option.color}>{option.label}</Tag>
              </Option>
            ))}
          </Select>
        </Space>
      </div>

      {/* 文档列表 */}
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <span style={{ marginLeft: 8 }}>加载文件中...</span>
          </div>
        ) : documents.length > 0 ? (
          <div className={styles.tableContainer}>
            <Card>
                            <Table
                columns={columns}
                dataSource={documents}
                rowKey="id"
                scroll={{ y: 'calc(100vh - 300px)' }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize
                  }));
                  const searchQuery = searchText.trim() || 'markdown';
                  fetchDocuments(page, searchQuery, statusFilter, pageSize);
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({
                    ...prev,
                    current: 1,
                    pageSize: size
                  }));
                  const searchQuery = searchText.trim() || 'markdown';
                  fetchDocuments(1, searchQuery, statusFilter, size);
                }
              }}
              loading={loading}
              size="middle"
            />
            </Card>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  暂无文件
                  {searchText && `，搜索 "${searchText}" 无结果`}
                </span>
              }
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={handleNewDocument}>
                创建第一个文档
              </Button>
            </Empty>
          </div>
        )}
      </div>

      {/* 模板选择器 */}
      <TemplateSelector
        visible={templateSelectorVisible}
        onClose={() => setTemplateSelectorVisible(false)}
        onSelect={handleTemplateSelect}
      />

      {/* 文档编辑Modal */}
      <DocumentModal
        visible={documentModalVisible}
        onClose={() => {
          setDocumentModalVisible(false);
          setEditingDocument(null);
        }}
        initialDocument={editingDocument}
        onSave={handleDocumentSave}
        attachments={documents}
        displayFiles={[]}
      />

      {/* 通用文件预览Modal */}
      <FilePreviewModal
        visible={previewVisible}
        onClose={() => {
          setPreviewVisible(false);
          setPreviewFile(null);
        }}
        file={previewFile}
      />

      {/* Markdown查看器 */}
      <MarkdownViewer
        visible={markdownViewerVisible}
        onClose={() => setMarkdownViewerVisible(false)}
        document={markdownViewerDocument}
        onEdit={handleMarkdownViewerEdit}
        onDownload={handleMarkdownViewerDownload}
      />
    </div>
  );
};

export default DocumentListPage;